import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { tokens } from '@/theme'

interface Props {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** Debounce in ms before onChange fires. Default 250. */
  debounceMs?: number
  /**
   * Fill the width offered instead of sitting at its natural 240px. For the
   * filter bars that lay themselves out — the initiatives row goes from one
   * flex line on a laptop to a grid on a phone, and a fixed width breaks
   * both ends of that.
   */
  fluid?: boolean
}

/**
 * Free-text search input. Debounces calls to `onChange` so each keystroke
 * doesn't refire the API list query — only the final pause does.
 */
export function SearchBox({
  value,
  onChange,
  placeholder = 'Search…',
  debounceMs = 250,
  fluid = false,
}: Props) {
  const [local, setLocal] = useState(value)

  // Keep local in sync if the parent resets (e.g. tab change clears search).
  useEffect(() => {
    setLocal(value)
  }, [value])

  // Debounce upstream notification.
  useEffect(() => {
    if (local === value) return
    const t = setTimeout(() => onChange(local), debounceMs)
    return () => clearTimeout(t)
  }, [local, value, debounceMs, onChange])

  return (
    <label
      style={{
        position: 'relative',
        display: fluid ? 'flex' : 'inline-flex',
        alignItems: 'center',
        gap: 8,
        ...(fluid && { width: '100%', minWidth: 0 }),
      }}
    >
      <Search
        size={14}
        strokeWidth={1.6}
        style={{ position: 'absolute', left: 10, color: tokens.ink4, pointerEvents: 'none' }}
      />
      <input
        type="search"
        value={local}
        onChange={e => setLocal(e.target.value)}
        placeholder={placeholder}
        style={{
          width: fluid ? '100%' : 240,
          minWidth: 0,
          padding: '8px 12px 8px 30px',
          fontFamily: tokens.fontDisplay,
          fontSize: 13,
          color: tokens.ink,
          background: tokens.paper,
          border: `1px solid ${tokens.line2}`,
          borderRadius: tokens.radiusMd,
          outline: 'none',
          transition: `border-color ${tokens.durFast} ${tokens.easeStandard}`,
        }}
        onFocus={e => {
          e.currentTarget.style.borderColor = tokens.gold
        }}
        onBlur={e => {
          e.currentTarget.style.borderColor = tokens.line2
        }}
      />
    </label>
  )
}
